import { StyledSelect } from "../../primitives/styled-select";
import * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Reorder, useDragControls } from "framer-motion";
import {
  ArrowRight,
  GripVertical,
  Image as ImageIcon,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import { cn } from "../../lib/cn";
import { Button } from "../../primitives/button";
import { AndoLogo } from "../AndoLogo";

/**
 * SlideOutlineEditor — per-slide HITL editor for ``document_create``
 * presentation proposals.
 *
 * Replaces the read-only ReactMarkdown outline that previously rendered
 * the slide list ("5 slide: 1. Title …") inside ``DocumentCreatePreview``.
 * The user can now edit every slide field the BE will feed back into the
 * Marp regenerator (BE reads ``modifications.slides`` → re-renders):
 *
 *   - ``title``        — slide heading (single-line input)
 *   - ``body``         — slide body markdown (multi-line, monospace)
 *   - ``layout``       — one of the BE-allowed layout names (select)
 *   - ``image_prompt`` — AI-image prompt, optional (input; null when blank)
 *
 * Fully CONTROLLED — the parent owns the ``slides`` array and re-renders on
 * every ``onChange``. No internal source-of-truth so the value can never
 * diverge from what gets committed into the execute ``modifications`` map.
 *
 * Reorder uses framer-motion's ``Reorder`` (already a workspace dep — no new
 * dependency added). Drag handle is the grip on the left of each card;
 * ``Reorder.Group`` emits the next ordered array which we forward verbatim
 * to ``onChange``.
 */

export interface SlideObj {
  readonly title: string;
  readonly body: string;
  readonly layout: string;
  readonly image_prompt: string | null;
  /** Hidden link to the approved structured slide; preserved across edits/reorder. */
  readonly source_index?: number;
}

export interface SlideOutlineEditorProps {
  /** Current slides array (controlled). */
  readonly slides: ReadonlyArray<SlideObj>;
  /** Allowed layout names (BE ``slide_list`` field ``options``). */
  readonly options: ReadonlyArray<string>;
  /** Emits the full next slides array on every edit / add / remove / reorder. */
  readonly onChange: (next: SlideObj[]) => void;
  /** Disable every input (e.g. while the parent's submit is in-flight). */
  readonly disabled?: boolean;
  /**
   * OPTIONAL — per-slide AI edit handler. When provided, each slide card
   * grows a compact AI instruction bar at the bottom; the parent runs the
   * BE round-trip and swaps the returned slide back into ``slides``. When
   * omitted (creation flow), NO extra UI is rendered (zero regression).
   */
  readonly onAiEditSlide?: (index: number, instruction: string) => Promise<void>;
  /**
   * Index of the slide whose AI edit is currently in flight. That card's
   * bar shows a spinner and disables itself; others stay interactive.
   */
  readonly aiBusyIndex?: number | null;
  readonly className?: string;
}

const inputCls =
  "w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-sm text-ink-1 placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ando-orange disabled:opacity-50";

/**
 * Default layout for a freshly-added slide: prefer "content" when the BE
 * offers it (the neutral body layout), else the first allowed option, else
 * an empty string (degenerate — the select renders no options).
 */
function defaultLayout(options: ReadonlyArray<string>): string {
  if (options.includes("content")) return "content";
  return options[0] ?? "";
}

function blankSlide(options: ReadonlyArray<string>): SlideObj {
  return { title: "", body: "", layout: defaultLayout(options), image_prompt: null };
}

/**
 * Row wrapper carrying a stable view-only id alongside the controlled
 * slide data. The id is NOT part of the contract (``SlideObj`` stays clean
 * for the wire) — it exists purely so React + framer-motion track each row
 * across reorder/insert/delete without an array-index key (which would
 * remount inputs and trip ``noArrayIndexKey``).
 */
interface SlideRow {
  readonly id: string;
  readonly slide: SlideObj;
}

interface SlideCardProps {
  readonly row: SlideRow;
  readonly index: number;
  readonly options: ReadonlyArray<string>;
  readonly disabled?: boolean;
  readonly onPatch: (patch: Partial<SlideObj>) => void;
  readonly onRemove: () => void;
  /** Per-slide AI handler (see ``SlideOutlineEditorProps.onAiEditSlide``). */
  readonly onAiEditSlide?: (index: number, instruction: string) => Promise<void>;
  /** Whether THIS card's AI edit is in flight. */
  readonly aiBusy?: boolean;
}

const SlideCard: React.FC<SlideCardProps> = ({
  row,
  index,
  options,
  disabled,
  onPatch,
  onRemove,
  onAiEditSlide,
  aiBusy = false,
}) => {
  const { i18n } = useLingui();
  const slide = row.slide;
  // Per-card drag controls so only the grip handle initiates a drag — the
  // text inputs stay selectable/editable without hijacking pointer events.
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={row}
      dragListener={false}
      dragControls={dragControls}
      className="rounded-xl border border-hairline bg-white/70 px-3 py-3"
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          aria-label={i18n._(msg`Trascina per riordinare la slide ${index + 1}`)}
          onPointerDown={(e) => {
            if (disabled) return;
            dragControls.start(e);
          }}
          disabled={disabled}
          className={cn(
            "mt-1 shrink-0 cursor-grab touch-none rounded-md p-1 text-ink-3",
            "transition-colors hover:bg-ando-orange-soft hover:text-ando-orange",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
            "active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-ando-orange-soft px-1.5 font-semibold text-[11px] text-ando-orange">
              {index + 1}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              disabled={disabled}
              aria-label={i18n._(msg`Rimuovi la slide ${index + 1}`)}
              className="h-7 px-2 text-ink-3 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor={`slide-${index}-title`}
              className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]"
            >
              {i18n._(msg`Titolo`)}
            </label>
            <input
              id={`slide-${index}-title`}
              type="text"
              className={inputCls}
              value={slide.title}
              placeholder={i18n._(msg`Titolo della slide`)}
              disabled={disabled}
              onChange={(e) => onPatch({ title: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor={`slide-${index}-body`}
              className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]"
            >
              {i18n._(msg`Contenuto`)}
            </label>
            <textarea
              id={`slide-${index}-body`}
              rows={4}
              className={cn(inputCls, "resize-y font-mono text-[12.5px] leading-relaxed")}
              value={slide.body}
              placeholder={i18n._(msg`- Punto uno\n- Punto due`)}
              disabled={disabled}
              onChange={(e) => onPatch({ body: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 flex-col gap-1">
              <label
                htmlFor={`slide-${index}-layout`}
                className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]"
              >
                {i18n._(msg`Layout`)}
              </label>
              <StyledSelect
                id={`slide-${index}-layout`}
                className={inputCls}
                value={slide.layout}
                disabled={disabled}
                onChange={(e) => onPatch({ layout: e.target.value })}
              >
                {/* Surface an unknown/legacy layout so the select never
                    silently drops the slide's current value. */}
                {!options.includes(slide.layout) && slide.layout ? (
                  <option value={slide.layout}>{slide.layout}</option>
                ) : null}
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </StyledSelect>
            </div>

            <div className="flex flex-1 flex-col gap-1">
              <label
                htmlFor={`slide-${index}-image`}
                className="flex items-center gap-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]"
              >
                <ImageIcon className="h-3 w-3" aria-hidden />
                {i18n._(msg`Immagine AI`)}
              </label>
              <input
                id={`slide-${index}-image`}
                type="text"
                className={inputCls}
                value={slide.image_prompt ?? ""}
                placeholder={i18n._(msg`Prompt immagine AI (opzionale)`)}
                disabled={disabled}
                // Empty string collapses to null so the BE treats the slide
                // as image-less (contract: image_prompt is string | null).
                onChange={(e) =>
                  onPatch({ image_prompt: e.target.value.trim() === "" ? null : e.target.value })
                }
              />
            </div>
          </div>

          {onAiEditSlide ? (
            <SlideAiBar
              index={index}
              busy={aiBusy}
              disabled={disabled}
              onSubmit={onAiEditSlide}
            />
          ) : null}
        </div>
      </div>
    </Reorder.Item>
  );
};
SlideCard.displayName = "SlideOutlineEditor.SlideCard";

interface SlideAiBarProps {
  readonly index: number;
  readonly busy: boolean;
  readonly disabled?: boolean;
  readonly onSubmit: (index: number, instruction: string) => Promise<void>;
}

/**
 * Compact per-slide AI instruction bar. Lives at the bottom of each
 * ``SlideCard`` only when the parent wires ``onAiEditSlide``. On submit
 * (Enter or the arrow button) it calls the parent handler; while ``busy``
 * it shows a spinner + disables itself, and clears its input on success.
 */
const SlideAiBar: React.FC<SlideAiBarProps> = ({
  index,
  busy,
  disabled = false,
  onSubmit,
}) => {
  const { i18n } = useLingui();
  const [instruction, setInstruction] = React.useState("");
  const inactive = busy || disabled;

  const submit = React.useCallback(async () => {
    const value = instruction.trim();
    if (value.length === 0 || inactive) return;
    await onSubmit(index, value);
    setInstruction("");
  }, [instruction, inactive, onSubmit, index]);

  return (
    <div
      className={cn(
        "mt-1 flex min-h-[44px] items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors",
        "border-hairline bg-white",
        "focus-within:border-ando-orange/60 focus-within:ring-2 focus-within:ring-ando-orange/30",
      )}
    >
      <AndoLogo size={16} className="shrink-0" />
      <input
        type="text"
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void submit();
          }
        }}
        disabled={inactive}
        placeholder={i18n._(msg`Chiedi all'AI di modificare questa slide… (es. rendi più formale, aggiungi un'immagine)`)}
        aria-label={i18n._(msg`Istruzione AI per la slide ${index + 1}`)}
        className="min-w-0 flex-1 bg-transparent text-[12.5px] text-ink-1 placeholder:text-ink-3 focus:outline-none disabled:opacity-60"
      />
      <button
        type="button"
        onClick={() => void submit()}
        disabled={inactive || instruction.trim().length === 0}
        aria-label={i18n._(msg`Applica modifica AI alla slide`)}
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white transition-[filter]",
          "bg-gradient-to-b from-ando-orange to-ando-orange-hover hover:brightness-[1.04]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
          "disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none",
        )}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden />
        ) : (
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
    </div>
  );
};
SlideAiBar.displayName = "SlideOutlineEditor.SlideAiBar";

let SLIDE_ROW_SEQ = 0;
function nextRowId(): string {
  SLIDE_ROW_SEQ += 1;
  return `slide-row-${SLIDE_ROW_SEQ}`;
}

export const SlideOutlineEditor: React.FC<SlideOutlineEditorProps> = ({
  slides,
  options,
  onChange,
  disabled,
  onAiEditSlide,
  aiBusyIndex = null,
  className,
}) => {
  const { i18n } = useLingui();
  // View-only row ids, kept in lockstep with the controlled ``slides`` length.
  // ``slides`` stays the source of truth for the data; ``rowIds`` only gives
  // React + framer-motion a stable per-row key across reorder/insert/delete.
  // When the parent shrinks/grows the array out-of-band (e.g. a BE refresh)
  // we reconcile by truncating / appending fresh ids.
  const [rowIds, setRowIds] = React.useState<string[]>(() =>
    slides.map(() => nextRowId()),
  );

  React.useEffect(() => {
    setRowIds((prev) => {
      if (prev.length === slides.length) return prev;
      if (prev.length > slides.length) return prev.slice(0, slides.length);
      const grown = prev.slice();
      while (grown.length < slides.length) grown.push(nextRowId());
      return grown;
    });
  }, [slides.length]);

  const rows = React.useMemo<SlideRow[]>(
    () => slides.map((slide, i) => ({ id: rowIds[i] ?? `slide-row-fallback-${i}`, slide })),
    [slides, rowIds],
  );

  const handleReorder = React.useCallback(
    (next: SlideRow[]) => {
      setRowIds(next.map((r) => r.id));
      onChange(next.map((r) => r.slide));
    },
    [onChange],
  );

  const handlePatch = React.useCallback(
    (index: number, patch: Partial<SlideObj>) => {
      onChange(slides.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    },
    [slides, onChange],
  );

  const handleRemove = React.useCallback(
    (index: number) => {
      setRowIds((prev) => prev.filter((_, i) => i !== index));
      onChange(slides.filter((_, i) => i !== index));
    },
    [slides, onChange],
  );

  const handleAdd = React.useCallback(() => {
    setRowIds((prev) => [...prev, nextRowId()]);
    onChange([...slides, blankSlide(options)]);
  }, [slides, options, onChange]);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <Reorder.Group
        axis="y"
        values={rows}
        onReorder={handleReorder}
        className="flex flex-col gap-3"
      >
        {rows.map((row, index) => (
          <SlideCard
            key={row.id}
            row={row}
            index={index}
            options={options}
            disabled={disabled}
            onPatch={(patch) => handlePatch(index, patch)}
            onRemove={() => handleRemove(index)}
            onAiEditSlide={onAiEditSlide}
            aiBusy={aiBusyIndex === index}
          />
        ))}
      </Reorder.Group>

      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={handleAdd}
        disabled={disabled}
        className="gap-1.5 self-start border-dashed text-ink-2 hover:border-ando-orange hover:text-ando-orange"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        {i18n._(msg`Aggiungi slide`)}
      </Button>
    </div>
  );
};
SlideOutlineEditor.displayName = "SlideOutlineEditor";
